import { useState } from 'react';
import { Link } from 'react-router';
import { roleHome, canUseKitchen, canVoidOrderLines } from '@/auth/roles';
import { useStaffSession } from '@/auth/react';
import { buttonClasses, Button } from '@/components/Button';
import { Badge } from '@/components/Badge';
import { cardClasses } from '@/components/Card';
import { EmptyState } from '@/components/EmptyState';
import { ErrorState } from '@/components/ErrorState';
import { Icon } from '@/components/Icon';
import { LoadingRegion, Skeleton } from '@/components/Skeleton';
import { useRealtimeState } from '@/realtime/react';
import type { AssistanceRequest, KitchenItem, KitchenTicket } from './api';
import { useAssistance, useKitchenLive, useResolveAssistance, useTickets } from './queries';
import { AvailabilityPanel } from './AvailabilityPanel';
import { formatClock, TicketCard, VoidSheet } from './parts';
import { assistanceLabel, BOARD_GROUPS, linesIn, locationText } from './wording';
import type { BoardStatus } from './wording';
import { Notice } from '@/components/Notice';
import { PageHeader } from '@/components/PageHeader';
import { ReadyState } from '@/components/ReadyState';
import { SegmentedTabs } from '@/components/SegmentedTabs';
import { useToastPlacement } from '@/components/Toast';

const EMPTY_COLUMN: Record<BoardStatus, { title: string; message: string }> = {
  SUBMITTED: { title: 'No new orders', message: 'New orders appear here as guests place them.' },
  IN_PREPARATION: {
    title: 'Nothing preparing',
    message: 'Lines you start from the New tab wait here until they are ready.',
  },
  READY: { title: 'Nothing ready', message: 'Lines marked ready wait here for a waiter.' },
};

function AssistancePanel({ requests }: { requests: AssistanceRequest[] }) {
  const resolve = useResolveAssistance();
  if (requests.length === 0) return null;
  return (
    <section
      aria-labelledby="assistance-title"
      className="rounded-surface border border-attention-line bg-attention-soft p-3"
    >
      <h2
        id="assistance-title"
        className="flex items-center gap-2 text-sm font-semibold text-attention-ink"
      >
        <Icon name="bell" size={18} />
        Assistance requested ({requests.length})
      </h2>
      <ul className="mt-2 flex flex-wrap gap-2">
        {requests.map((request) => {
          const where = locationText(request.location_type, request.location_label);
          const what = assistanceLabel(request.type);
          const saving = resolve.isPending && resolve.variables === request.request_id;
          return (
            <li
              key={request.request_id}
              className="flex min-w-0 items-center gap-3 rounded-control bg-white px-3 py-2"
            >
              <div className="min-w-0">
                <p className="text-base font-semibold break-words text-ink">
                  {where} · {what}
                </p>
                <p className="text-sm break-words text-muted">
                  {formatClock(request.created_at)}
                  {request.note ? ` · ${request.note}` : ''}
                </p>
              </div>
              <Button
                variant="secondary"
                loading={saving}
                disabled={resolve.isPending}
                aria-label={`Mark done: ${what}, ${where}`}
                onClick={() => resolve.mutate(request.request_id)}
              >
                Done
              </Button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

function BoardSkeleton() {
  return (
    <LoadingRegion slowHint label="Loading kitchen orders">
      <div className="flex flex-col gap-3">
        <Skeleton className="h-12" />
        <Skeleton className="h-48" />
        <Skeleton className="h-48" />
      </div>
    </LoadingRegion>
  );
}

function KitchenBoard({ canVoid }: { canVoid: boolean }) {
  const live = useRealtimeState() === 'live';
  const tickets = useTickets();
  const assistance = useAssistance();
  const [shown, setShown] = useState<BoardStatus>('SUBMITTED');
  // The void sheet lives here so a refusal stays on screen even when the line's ticket leaves the tab.
  const [voiding, setVoiding] = useState<{ item: KitchenItem; ticket: KitchenTicket } | null>(null);
  const openVoid = (item: KitchenItem, ticket: KitchenTicket) => setVoiding({ item, ticket });

  const list: KitchenTicket[] = tickets.data ?? [];
  // A tab lists every order with at least one line in that state, and on it only those lines; an order
  // with a new line and a preparing line is on both tabs. Counts are orders, not lines.
  const byStatus = (status: BoardStatus) =>
    list.filter((ticket) => linesIn(ticket, status).length > 0);
  const known = BOARD_GROUPS.map((group) => group.status as string);
  const other = list.filter(
    (ticket) =>
      !known.includes(ticket.status) &&
      !BOARD_GROUPS.some((group) => linesIn(ticket, group.status).length > 0),
  );
  const refreshing = tickets.isFetching && !tickets.isPending;

  // One status at a time: the tabs stay in reach while a long list scrolls. The same row is drawn when the
  // board is empty, so the first ticket does not move it and the chosen tab survives; nothing below depends
  // on it while there is nothing to show.
  const tabs = (
    <div className="sticky top-0 z-10 -mx-4 bg-canvas px-4 py-2 md:-mx-6 md:px-6">
      <SegmentedTabs
        label="Show orders"
        semantics="buttons"
        fill
        options={BOARD_GROUPS.map((group) => ({
          id: group.status,
          label: group.title,
          count: byStatus(group.status).length,
        }))}
        value={shown}
        onChange={setShown}
      />
    </div>
  );

  return (
    // One column, oldest first, never columns (product rule). The width cap lives on the screen wrapper
    // (`KitchenScreen`), so the board and the availability view share one centred column.
    <section aria-labelledby="kitchen-title" className="flex flex-col gap-4">
      <PageHeader
        id="kitchen-title"
        title="Kitchen orders"
        actions={
          <>
            {/* The connection banner above the page already says when live updates are paused. */}
            <p role="status" aria-live="polite" className="text-sm text-muted">
              {refreshing ? 'Updating…' : ''}
            </p>
            <Button variant="secondary" loading={refreshing} onClick={() => void tickets.refetch()}>
              Refresh
            </Button>
          </>
        }
      />

      {tickets.isError && tickets.data && (
        <Notice
          tone="warn"
          action={
            <Button variant="secondary" onClick={() => void tickets.refetch()}>
              Try again
            </Button>
          }
        >
          Couldn&apos;t refresh the orders. Showing the last list received.
        </Notice>
      )}

      {assistance.data && <AssistancePanel requests={assistance.data} />}

      {tickets.isPending ? (
        <BoardSkeleton />
      ) : tickets.isError && !tickets.data ? (
        <ErrorState error={tickets.error} onRetry={() => void tickets.refetch()} />
      ) : list.length === 0 ? (
        <>
          {tabs}
          <ReadyState
            id="kitchen-ready-title"
            title="Kitchen is ready"
            message="New orders will appear here automatically."
          >
            {live && <Badge tone="success">Live</Badge>}
          </ReadyState>
        </>
      ) : (
        <>
          {tabs}
          {BOARD_GROUPS.filter((group) => group.status === shown).map((group) => {
            const column = byStatus(group.status);
            return (
              <section
                key={group.status}
                aria-labelledby={`column-${group.status}`}
                className="flex min-w-0 flex-col gap-3"
              >
                <h2 id={`column-${group.status}`} className="sr-only">
                  {group.title} ({column.length})
                </h2>
                {column.length === 0 ? (
                  <div role="status" className={cardClasses({ padding: 'none' })}>
                    <EmptyState
                      icon="orders"
                      title={EMPTY_COLUMN[group.status].title}
                      message={EMPTY_COLUMN[group.status].message}
                    />
                  </div>
                ) : (
                  <div className="flex flex-col gap-3">
                    {column.map((ticket) => (
                      <TicketCard
                        key={ticket.order_id}
                        ticket={ticket}
                        receivedAt={tickets.dataUpdatedAt}
                        canVoid={canVoid}
                        shown={group.status}
                        onVoid={openVoid}
                      />
                    ))}
                  </div>
                )}
              </section>
            );
          })}
          {other.length > 0 && (
            <section aria-labelledby="column-other" className="flex flex-col gap-3">
              <h2 id="column-other" className="text-base font-semibold text-ink">
                Other ({other.length})
              </h2>
              <div className="flex flex-col gap-3">
                {other.map((ticket) => (
                  <TicketCard
                    key={ticket.order_id}
                    ticket={ticket}
                    receivedAt={tickets.dataUpdatedAt}
                    canVoid={canVoid}
                    onVoid={openVoid}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}
      {voiding && (
        <VoidSheet item={voiding.item} ticket={voiding.ticket} onClose={() => setVoiding(null)} />
      )}
    </section>
  );
}

/**
 * The kitchen display. The role check only decides what to draw; the backend decides every request, and
 * a refusal (403) is shown in words like any other error.
 */
export function KitchenPage() {
  const session = useStaffSession();
  const role = session?.user.role;
  if (!role || !canUseKitchen(role)) {
    return (
      <EmptyState
        icon="alert"
        title="Not allowed"
        message="You don't have access to the kitchen display."
        action={
          role ? (
            <Link to={roleHome(role)} className={buttonClasses({ variant: 'secondary' })}>
              Go to your home screen
            </Link>
          ) : undefined
        }
      />
    );
  }
  return <KitchenScreen canVoid={canVoidOrderLines(role)} />;
}

const VIEWS = [
  { id: 'orders', label: 'Orders' },
  { id: 'availability', label: 'Menu availability' },
] as const;
type View = (typeof VIEWS)[number]['id'];

/** Orders (the board) and the sold-out switch. One live subscription serves both views. */
function KitchenScreen({ canVoid }: { canVoid: boolean }) {
  useKitchenLive();
  // Toasts float at the top here: a bottom toast would cover the footer of the card it reports on.
  useToastPlacement('top');
  const [view, setView] = useState<View>('orders');
  // One centred column for everything on this screen: the view strip, the title row, the panels and the
  // tickets share the same edges. Capped so a single ticket on a wide screen keeps its dish names and its
  // action cluster within one line of sight; below the cap (768, 1024) it is simply the full width.
  return (
    <div className="mx-auto flex w-full max-w-5xl flex-col gap-4">
      <SegmentedTabs
        label="Kitchen views"
        idPrefix="kitchen-tab"
        panelId="kitchen-panel"
        options={VIEWS}
        value={view}
        onChange={setView}
      />
      <div id="kitchen-panel" role="tabpanel" aria-labelledby={`kitchen-tab-${view}`}>
        {view === 'orders' ? <KitchenBoard canVoid={canVoid} /> : <AvailabilityPanel />}
      </div>
    </div>
  );
}
